import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import PanelBlock, { PanelEyebrow } from '../PanelBlock';
import DatapointRows from './DatapointRows';
import SectionAssessment from './SectionAssessment';
import { LABOKLIN_RED } from '../../../theme/laboklinTheme';
import {
  Assessment,
  AssessmentSection,
  ReportScore,
  RunPanels,
  SectionStability,
  SECTION_LABELS,
  gradeOf,
  RunStep,
} from '../../../types/labConsistency';

/**
 * One graded section: its numbers, its datapoints, and the veterinarian's verdict.
 *
 * Every number rendered here is the STORED one. The page never recomputes a metric the
 * server also computes - that is what stops this view and the markdown scorecard
 * disagreeing about the same run. It derives only presence from the run items it
 * already holds, which is membership rather than a measure.
 */

const GRADE_COLOUR = {
  good: 'rgb(30, 130, 60)',
  warn: 'rgb(190, 130, 20)',
  bad: LABOKLIN_RED,
} as const;

interface ConsistencySectionProps {
  section: AssessmentSection;
  stability: SectionStability;
  /**
   * The same measure over the diagnostic engine's own output.
   *
   * Deliberately not rendered here. It is how WE tell an unstable ranking from an
   * unstable review, which is a debugging question - a veterinarian reading this page
   * is being asked whether the answer is clinically sound, and a second percentage
   * about an internal component only competes with that. It lives under "Why did this
   * vary?" instead.
   */
  engine?: SectionStability;
  runs: RunPanels[];
  report: ReportScore;
  step: RunStep;
  assessment?: Assessment;
  proseFor: (run: RunPanels, itemId: number) => string | null;
  rankFor?: (run: RunPanels, itemId: number) => number | null;
  emptyMessage: string;
}

/**
 * A measure said in words, with the number after it rather than instead of it.
 *
 * "presence 0.80" tells a veterinarian nothing; "shown in 4 of 5 runs" tells them
 * exactly what happened and can be checked by eye. The ratio stays because it is what
 * the markdown scorecard prints and the two must be reconcilable, but it is secondary.
 */
const Measure: React.FC<{
  label: string;
  value: number;
  metric: 'presence' | 'content';
  detail: string;
  help: string;
}> = ({ label, value, metric, detail, help }) => (
  <Tooltip title={<Typography sx={{ fontSize: 12, p: 0.5 }}>{help}</Typography>} arrow placement="top">
    <Box sx={{ minWidth: 0, cursor: 'help' }}>
      <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', borderBottom: '1px dotted rgba(0,0,0,0.3)', display: 'inline-block' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: GRADE_COLOUR[gradeOf(value, metric)] }}>
        {detail}
      </Typography>
    </Box>
  </Tooltip>
);

export const ConsistencySection: React.FC<ConsistencySectionProps> = ({
  section,
  stability,
  runs,
  report,
  step,
  assessment,
  proseFor,
  rankFor,
  emptyMessage,
}) => {
  const total = stability.runs;
  const settled = stability.items.filter((i) => i.runs === total).length;
  // Presence is max(p, 1-p); the runs that actually carried the section are the ones
  // with any item in them, which is what a veterinarian would count.
  const shownIn = new Set(stability.items.flatMap((i) => i.presentIn)).size;

  return (
    <PanelBlock>
      <PanelEyebrow label={SECTION_LABELS[section]} state={`${total} runs`} />

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1.25 }}>
        <Measure
          label="Was it there?"
          value={stability.presenceAgreement}
          metric="presence"
          help={`Whether this section appeared at all. It should either be there every time or not at all — appearing in some runs and not others is the inconsistency, whatever it says when it does appear.`}
          detail={
            total === 0
              ? 'No runs to compare'
              : shownIn === total
                ? `Shown in all ${total} runs`
                : shownIn === 0
                  ? 'Not shown in any run'
                  : `Shown in ${shownIn} of ${total} runs`
          }
        />
        <Measure
          label="Was it the same?"
          value={stability.contentAgreement}
          metric="content"
          help={`Of everything named across all ${total} runs, how much appeared every single time. The rest came and went between runs.`}
          detail={
            stability.items.length === 0
              ? '—'
              : `${settled} of ${stability.items.length} appeared every time`
          }
        />
        {section === 'differentials' && stability.items.length > 0 && (
          <Measure
            label="Same one first?"
            value={stability.leadAgreement}
            metric="content"
            help="How often the same diagnosis came first. The top of the list is what gets read, so it moving matters more than the tail moving."
            detail={`Top of the list matched in ${Math.round(stability.leadAgreement * total)} of ${total}`}
          />
        )}
      </Box>

      <DatapointRows
        items={stability.items}
        runs={runs}
        proseFor={proseFor}
        rankFor={rankFor}
        emptyMessage={emptyMessage}
      />

      <SectionAssessment
        batchId={report.batchId}
        reportNo={report.reportNo}
        step={step}
        section={section}
        contentHash={report.contentHash}
        existing={assessment}
      />
    </PanelBlock>
  );
};

export default ConsistencySection;
